import { OrderChargeStatusEnum } from "@dashboard/graphql";
import { PillStatusType } from "@dashboard/misc";
import { defineMessages, IntlShape } from "react-intl";

export const paymentStatusPillMessages = defineMessages({
  refundPending: {
    defaultMessage: "Refund Pending",
    id: "8yE3HA",
    description: "charge status",
  },
  refunded: {
    defaultMessage: "Refunded",
    id: "oKLGJi",
    description: "charge status",
  },
  partiallyRefunded: {
    defaultMessage: "Partially Refunded",
    id: "L4U41b",
    description: "charge status",
  },
  overcharged: {
    defaultMessage: "Overcharged",
    id: "BXKn/d",
    description: "charge status",
  },
});

export type DerivedPaymentPillState =
  | "refund-pending"
  | "refunded"
  | "partially-refunded"
  | "overcharged"
  | "default";

export interface OrderForPaymentPill {
  chargeStatus: OrderChargeStatusEnum;
  totalCharged?: { amount: number } | null;
  totalRefunded?: { amount: number } | null;
  totalGrantedRefund?: { amount: number } | null;
}

/**
 * A refund is granted immediately, but only settles (totalRefunded) once the
 * gateway confirms it - until then, chargeStatus/paymentStatus don't reflect
 * it either (see refundConfirmation.ts), so derive the pill state straight
 * from the running totals instead of trusting either of those fields.
 */
export const derivePaymentPillState = (order: OrderForPaymentPill): DerivedPaymentPillState => {
  const charged = order.totalCharged?.amount ?? 0;
  const refunded = order.totalRefunded?.amount ?? 0;
  const granted = order.totalGrantedRefund?.amount ?? 0;

  if (granted > refunded) {
    return "refund-pending";
  }

  if (charged === 0 && refunded > 0) {
    return "refunded";
  }

  if (charged > 0 && refunded > 0) {
    return "partially-refunded";
  }

  if (order.chargeStatus === OrderChargeStatusEnum.OVERCHARGED) {
    return "overcharged";
  }

  return "default";
};

export const getDerivedPaymentPillLabel = (
  state: Exclude<DerivedPaymentPillState, "default">,
  intl: IntlShape,
): { label: string; status: PillStatusType } => {
  switch (state) {
    case "refund-pending":
      return {
        label: intl.formatMessage(paymentStatusPillMessages.refundPending),
        status: "warning",
      };
    case "refunded":
      return {
        label: intl.formatMessage(paymentStatusPillMessages.refunded),
        status: "info",
      };
    case "partially-refunded":
      return {
        label: intl.formatMessage(paymentStatusPillMessages.partiallyRefunded),
        status: "info",
      };
    case "overcharged":
      return {
        label: intl.formatMessage(paymentStatusPillMessages.overcharged),
        status: "warning",
      };
  }
};
